import { AfterViewInit, Component, HostListener, NgZone, OnDestroy, OnInit } from '@angular/core';
import { WordpressService } from '../../../services/wordpress.service';
import { BlinkProductionCard, ClientLogoCard, ServiceCard } from '../../../models/service.model';
import { gsap } from 'gsap';

declare const $: any;

@Component({
  selector: 'app-home-three',
  templateUrl: './home-three.component.html',
  styleUrls: ['./home-three.component.scss']
})
export class HomeThreeComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly heroSlides = [
    { image: 'assets/img/home-three/home-banner.jpg' },
    { image: 'assets/img/home-three/blink-productions/vission.jpg' }
  ];
  activeHeroSlide = 0;
  heroAutoplayPaused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  private heroAutoplayTimer?: number;
  private readonly heroAutoplayInterval = 6500;
  services: ServiceCard[] = [];
  loading = true;
  error: string | null = null;
  skeletonItems = Array.from({ length: 6 });
  clients: ClientLogoCard[] = [];
  clientsLoading = true;
  clientsError: string | null = null;
  clientSkeletonItems = Array.from({ length: 6 });
  productions: BlinkProductionCard[] = [];
  productionsLoading = true;
  productionsError: string | null = null;
  productionSkeletonItems = Array.from({ length: 3 });
  openingPanels = Array.from({ length: 9 });
  openingTransitionActive = true;
  // Used only when a service genuinely has no WordPress image (acf.service_icon empty and
  // no featured media) — a single neutral icon, not chosen by position or title, so it
  // never masquerades as real per-service WordPress data.
  readonly fallbackServiceIcon = 'bx bx-briefcase';
  private heroTimeline?: gsap.core.Timeline;
  private openingTimeline?: gsap.core.Timeline;
  private reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  constructor(private wordpressService: WordpressService, private ngZone: NgZone) { }

  ngOnInit(): void {
    this.loadServices();
    this.loadClients();
    this.loadBlinkProductions();
  }

  private loadServices(): void {
    this.wordpressService.getServices().subscribe({
      next: services => {
        this.services = services;
        this.loading = false;
      },
      error: err => {
        this.error = this.formatError(err);
        this.loading = false;
      }
    });
  }

  private loadClients(): void {
    this.wordpressService.getClients().subscribe({
      next: clients => {
        this.clients = clients;
        this.clientsLoading = false;
      },
      error: err => {
        this.clientsError = this.formatError(err, 'Unable to fetch clients from WordPress.');
        this.clientsLoading = false;
      }
    });
  }

  private loadBlinkProductions(): void {
    this.wordpressService.getBlinkProductions().subscribe({
      next: productions => {
        this.productions = productions;
        this.productionsLoading = false;
      },
      error: err => {
        this.productionsError = this.formatError(err, 'Unable to fetch Blink Productions content from WordPress.');
        this.productionsLoading = false;
      }
    });
  }

  trackByService(index: number, service: ServiceCard): number {
    return service.id;
  }

  trackByClient(index: number, client: ClientLogoCard): number {
    return client.id;
  }

  trackByProduction(index: number, item: BlinkProductionCard): number {
    return item.id;
  }

  /* True only for a final item left alone on its own row in the 2-column (col-sm-6)
     layout, so it gets centered the same way the original 3rd static item was —
     generalizes correctly for any item count, not just 3. */
  needsSmOffset(index: number, total: number): boolean {
    return index === total - 1 && index % 2 === 0;
  }

  getServiceHref(service: ServiceCard): string | undefined {
    return service.link || undefined;
  }

  private formatError(err: any, fallback = 'Unable to fetch services from WordPress.'): string {
    if (err?.status) {
      return `${err.status} ${err.statusText || 'Error'}${err.error?.message ? ' - ' + err.error.message : ''}`;
    }

    return err?.message || fallback;
  }

  ngAfterViewInit(): void {
    // Start hero entrance immediately (no full-screen opening panels)
    this.openingTransitionActive = false;
    this.playHeroEntrance();
    this.startHeroAutoplay();
    // Owl can initialize before Angular renders the section; initialize here once DOM is ready.
    setTimeout(() => {
      const $clientsSlider = $('.testimonial-area .testimonial-slider');

      if (
        $clientsSlider.length &&
        !$clientsSlider.hasClass('owl-loaded') &&
        typeof $clientsSlider.owlCarousel === 'function'
      ) {
        $clientsSlider.owlCarousel({
          items: 1,
          loop: true,
          margin: 15,
          singleItem: true,
          nav: false,
          dots: false,
          smartSpeed: 1000,
          autoplay: true,
          autoplayTimeout: 4000,
          autoplayHoverPause: true
        });
      }
    }, 0);
  }

  ngOnDestroy(): void {
    this.stopHeroAutoplay();
    this.heroTimeline?.kill();
    this.openingTimeline?.kill();
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (document.hidden) {
      this.stopHeroAutoplay();
    } else {
      this.startHeroAutoplay();
    }
  }

  selectHeroSlide(index: number): void {
    this.activeHeroSlide = (index + this.heroSlides.length) % this.heroSlides.length;
    this.restartHeroAutoplay();
  }

  previousHeroSlide(): void {
    this.selectHeroSlide(this.activeHeroSlide - 1);
  }

  nextHeroSlide(): void {
    this.selectHeroSlide(this.activeHeroSlide + 1);
  }

  toggleHeroAutoplay(): void {
    this.heroAutoplayPaused = !this.heroAutoplayPaused;
    this.restartHeroAutoplay();
  }

  private startHeroAutoplay(): void {
    if (this.heroAutoplayPaused || document.hidden || this.heroAutoplayTimer || this.heroSlides.length < 2) {
      return;
    }
    // The interval stays outside Angular's change detection cycle. Enter the
    // zone only when the active slide actually changes.
    this.ngZone.runOutsideAngular(() => {
      this.heroAutoplayTimer = window.setInterval(() => {
        if (!document.hidden) {
          this.ngZone.run(() => {
            this.activeHeroSlide = (this.activeHeroSlide + 1) % this.heroSlides.length;
          });
        }
      }, this.heroAutoplayInterval);
    });
  }

  private stopHeroAutoplay(): void {
    if (this.heroAutoplayTimer !== undefined) {
      window.clearInterval(this.heroAutoplayTimer);
      this.heroAutoplayTimer = undefined;
    }
  }

  private restartHeroAutoplay(): void {
    this.stopHeroAutoplay();
    this.startHeroAutoplay();
  }

  private playHeroEntrance(): void {
    if (this.reducedMotion) {
      return;
    }

    // Reveal hero elements then animate thin vertical lines downward from the top
    this.heroTimeline = gsap.timeline({ defaults: { ease: 'power3.out' } })
      .fromTo('.banner-area-three .banner-content h1', { autoAlpha: 0, yPercent: 8 }, { autoAlpha: 1, yPercent: 0, duration: 0.72 })
      .fromTo('.banner-area-three .banner-social', { autoAlpha: 0, x: -18 }, { autoAlpha: 1, x: 0, duration: 0.55 }, '-=0.48')
      .fromTo('.banner-area-three .line', { autoAlpha: 0, scaleY: 0, transformOrigin: 'top' }, { autoAlpha: 1, scaleY: 1, duration: 0.7, stagger: 0.06 }, '-=0.5');
  }

  private startHomeOpeningTransition(): void {
    if (this.reducedMotion) {
      this.openingTransitionActive = false;
      this.playHeroEntrance();
      return;
    }

    const begin = () => this.playHomeOpeningTransition();
    const loader = document.querySelector('.loader');

    if (!loader) {
      begin();
      return;
    }

    const delayForPreloader = () => window.setTimeout(begin, 600);
    if (document.readyState === 'complete') {
      delayForPreloader();
    } else {
      window.addEventListener('load', delayForPreloader, { once: true });
    }
  }

  private playHomeOpeningTransition(): void {
    const panels = '.home-opening-transition .home-opening-panel';
    this.openingTimeline = gsap.timeline({ defaults: { ease: 'power4.inOut' } })
      .fromTo(panels, { yPercent: -110 }, { yPercent: 0, duration: 0.48, stagger: 0.045 })
      .to(panels, { yPercent: 110, duration: 0.64, stagger: 0.045 }, '+=0.06')
      .call(() => this.ngZone.run(() => {
        this.openingTransitionActive = false;
        this.playHeroEntrance();
      }));
  }
}
